import React, { createContext, useContext, useState, useCallback } from 'react';
import { authenticate, createUser, getCurrentUser, updateUser, forgotPassword, resetPassword as resetPasswordAPI } from '../api';
import { useCompany } from '../contexts/CompanyContext';
import { useSnackbar } from 'notistack';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  isOwner?: boolean;
}

interface UserContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ requires2FA: boolean; }>;
  verifyTwoFactor: (code: string) => Promise<void>;
  logout: () => void;
  requestPasswordReset: (email: string) => Promise<void>;
  register: (firstName: string, lastName: string, email: string, company: string) => Promise<void>;
  update: (data: any) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setCurrent: setCurrentCompany, create: createCompany, update: updateCompany } = useCompany();
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState<User | null>(null);
  const [twoFactorPending, setTwoFactorPending] = useState(false);

  const setUserContext = async (userData: User | null) => {
    if(userData) {
      await setUser(userData);
      await localStorage.setItem('user', JSON.stringify(userData));
    }else {
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }

    await await setCurrentCompany();
  }

  const login = useCallback(async (email: string, password: string) => {

    const response = (await authenticate({ email, password }));
    const token = response?.token;
    
    if (!token) {
        return {
            requires2FA: false
        }
    }
    await localStorage.setItem('token', token);

    const user = await getCurrentUser()

    await setUserContext(user);
    
    return {
      requires2FA: false
    }
  }, []);

  const verifyTwoFactor = useCallback(async (code: string) => {
    try {
      // Simulate API call
      await new Promise<void>((resolve, reject) => {
        // if (code === '123456') {
          const user = {
            id: '1',
            email: 'admin@example.com',
            firstName: 'Admin',
            lastName: 'User'
          }

          setUserContext(user);
          localStorage.setItem('user', JSON.stringify(user));
          setTwoFactorPending(false);
          resolve();
        // } else {
        //   reject(new Error('Invalid two-factor code'));
        // }
      });
    } catch (error) {
      console.error('Two-factor verification failed:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setUserContext(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setTwoFactorPending(false);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      // Simulate API call
      await forgotPassword({email})
    } catch (error) {
      console.error('Password reset request failed:', error);
      throw error;
    }
  }, []);

  const register = useCallback(async (firstName: string, lastName: string, email: string, company: string) => {
    try {
      const newCompany = await createCompany({name: company});
      const newUser = await createUser({firstName, lastName, email, company: newCompany.id});
      await updateCompany({...newCompany, owner: newUser.id});
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    try {

      await resetPasswordAPI({password, token});
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }, []);

  const update = useCallback(async (data: any) => {
    setUserContext(await updateUser(data));
    enqueueSnackbar('User updated successfully', 
      { 
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' }
      });
  }, []);

  const value = {
    user,
    login,
    verifyTwoFactor,
    logout,
    requestPasswordReset,
    register,
    update,
    resetPassword,
  };

  const setUserFromStorage = useCallback(async () => {
    setUserContext(JSON.parse(localStorage.getItem('user')!));
  }, []);

  if(localStorage.getItem('user') && !user) {
    setUserFromStorage();
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};